import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const FEATURES = [
  { icon: '🚫', title: 'Zero ads', desc: 'Clean experience on web and mobile, forever' },
  { icon: '⚡', title: 'Instant alerts', desc: 'Push notification the moment a saved charger changes status' },
  { icon: '📍', title: 'Unlimited saved chargers', desc: 'Save your entire regular route, not just 3' },
  { icon: '🏆', title: 'Premium badge', desc: 'Shown on leaderboard as a recognised top contributor' },
  { icon: '📊', title: 'Reliability history', desc: '30-day reliability chart per charger' },
];

export default function PremiumPage() {
  const [loading, setLoading] = useState(false);
  const loggedIn = !!localStorage.getItem('evapp_token');
  const navigate = useNavigate();

  const upgrade = async () => {
    if (!loggedIn) { navigate('/login'); return; }
    setLoading(true);
    try {
      const res = await api.post('/billing/checkout');
      window.location.href = res.data.checkout_url;
    } catch (err) {
      if (err.response?.status === 503) {
        toast('Billing coming soon — earn Premium free with 10 reports below!', { icon: '💡' });
      } else if (err.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Could not start checkout');
      }
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', paddingTop: 56, fontFamily: 'Inter, sans-serif' }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(180deg, var(--slate) 0%, var(--navy) 100%)',
        borderBottom: '1px solid var(--border)',
        padding: '52px 20px 44px',
        textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-flex', width: 64, height: 64, borderRadius: 18,
          background: 'linear-gradient(135deg,#00D4AA,#0099FF)',
          alignItems: 'center', justifyContent: 'center',
          fontSize: 30, marginBottom: 20, boxShadow: '0 0 40px rgba(0,212,170,0.3)',
        }}>⚡</div>
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.6px', marginBottom: 10, color: 'var(--text-1)' }}>
          ChargeSure <span style={{ color: 'var(--teal)' }}>Premium</span>
        </h1>
        <p style={{ fontSize: 16, color: 'var(--text-2)', maxWidth: 440, margin: '0 auto' }}>
          For EV drivers who can't afford to arrive at a broken charger.
        </p>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '32px 16px 60px' }}>
        {/* Price card */}
        <div style={{
          background: 'linear-gradient(135deg, var(--slate) 0%, #1a2535 100%)',
          border: '1px solid var(--teal-mid)',
          borderRadius: 20, padding: '32px 28px',
          textAlign: 'center', marginBottom: 20,
          position: 'relative', overflow: 'hidden',
          boxShadow: '0 0 60px rgba(0,212,170,0.08)',
        }}>
          {/* Decorative glow */}
          <div style={{
            position: 'absolute', top: -60, right: -60,
            width: 180, height: 180, borderRadius: '50%',
            background: 'var(--teal)', opacity: 0.05, filter: 'blur(40px)',
            pointerEvents: 'none',
          }} />

          <div style={{ fontSize: 13, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>Monthly plan</div>
          <div style={{ fontSize: 56, fontWeight: 900, color: 'var(--teal)', fontFamily: 'JetBrains Mono, monospace', lineHeight: 1, marginBottom: 6 }}>
            €3.99
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 28 }}>per month · cancel anytime · no commitment</div>

          <button onClick={upgrade} disabled={loading} style={{
            width: '100%', padding: '15px 0', borderRadius: 12, border: 'none',
            background: 'linear-gradient(135deg,#00D4AA,#0099FF)',
            color: '#F0F4FF', fontWeight: 800, fontSize: 16,
            cursor: 'pointer', opacity: loading ? 0.7 : 1,
            boxShadow: '0 4px 20px rgba(0,212,170,0.3)',
            letterSpacing: '-0.2px',
          }}>
            {loading ? 'Redirecting…' : loggedIn ? 'Upgrade with card →' : 'Sign in to upgrade →'}
          </button>

          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-3)' }}>
            🔒 Secure payment via Stripe · SSL encrypted
          </div>
        </div>

        {/* Features */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {FEATURES.map(({ icon, title, desc }) => (
            <div key={title} style={{
              display: 'flex', gap: 14, alignItems: 'flex-start',
              background: 'var(--slate)', border: '1px solid var(--border)',
              borderRadius: 12, padding: '14px 16px',
              transition: 'border-color 0.2s',
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: 'var(--navy)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 18,
              }}>{icon}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-1)', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.5 }}>{desc}</div>
              </div>
              <div style={{ marginLeft: 'auto', color: 'var(--teal)', flexShrink: 0, fontSize: 16 }}>✓</div>
            </div>
          ))}
        </div>

        {/* Free path */}
        <div style={{
          background: 'var(--teal-dim)', border: '1px solid var(--teal-mid)',
          borderRadius: 14, padding: '20px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>💡</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--teal)', marginBottom: 6 }}>
            Get Premium completely free
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 14 }}>
            Submit 10 community reports on any chargers and Premium unlocks automatically. No payment, no credit card.
          </div>
          <Link to="/" style={{
            display: 'inline-block', padding: '9px 20px', borderRadius: 8,
            border: '1px solid var(--teal-mid)', color: 'var(--teal)',
            fontSize: 13, fontWeight: 600, textDecoration: 'none',
          }}>
            Start reporting on the map →
          </Link>
        </div>

        <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
          India pricing: ₹99/month (coming soon via Razorpay).<br />
          Questions? <a href="mailto:support@chargesure.app" style={{ color: 'var(--teal)', textDecoration: 'none' }}>support@chargesure.app</a>
        </p>
      </div>
    </div>
  );
}
